import { useEffect, useState } from "react";
import { BarChart3, CalendarRange, ChevronDown, ChevronRight, Headset, Layers, RefreshCw, Table2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";
import { ComparisonChart } from "@/components/Charts";
import { useAuth } from "@/context/AuthContext";
import { eur, num, MESI, dateIt } from "@/lib/format";

const pct = (v) => (v === null || v === undefined ? "–" : `${v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
const meseLabel = (m) => MESI[parseInt(m.slice(5), 10) - 1].slice(0, 3);
const slug = (s) => String(s).replace(/[^a-z0-9]+/gi, "-").toLowerCase();
const COLORI = { TOTALE: "#0F172A", LIGHT: "#0018A8", PREMIUMBANK: "#0F766E", CORPORATE: "#C8102E", "SPOT / ERIKA": "#B45309", WELCOME: "#7C3AED", ALTRO: "#94A3B8" };

function Toggle({ valore, onChange, opzioni, testId }) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid={testId}>
      {opzioni.map(([v, Icon, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
          className={`inline-flex items-center gap-1 px-3 py-1.5 transition-colors duration-200 ${valore === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
          <Icon size={13} /> {l}
        </button>
      ))}
    </div>
  );
}

function CellaMese({ r, chiuso }) {
  if (!r || (!r.clienti && !r.caricate)) return <td className="px-2 py-2 text-center text-slate-300">–</td>;
  return (
    <td className={`px-2 py-2 text-center font-mono ${chiuso ? "" : "text-slate-400"}`}
      title={`${num(r.caricate)} caricate su ${num(r.clienti)} clienti che hanno risposto · liquidate ${pct(r.redemption_liquidate)}`}>
      <div className={chiuso ? "text-[#0018A8] font-semibold" : ""}>{pct(r.redemption)}</div>
      <div className="text-[10px] text-slate-400">{num(r.caricate)}/{num(r.clienti)}</div>
    </td>
  );
}

const Nome = ({ r, wave, sotto }) => (
  <span className={`inline-flex items-center gap-2 ${sotto ? "pl-6 text-slate-600 font-normal" : "font-medium text-slate-900"}`}>
    {wave && <span className="w-2 h-2 rounded-full" style={{ background: COLORI[r.nome] || "#64748B" }} />}{r.nome}
  </span>
);

/** Una riga della tabella (mensile o rolling) + eventuali sotto-righe per wave dell'operatore. */
function Righe({ righe, periodo, mesi, chiusi, agenzia, wave, espandibile, testPrefix, soglia }) {
  const [aperti, setAperti] = useState({});
  const grigia = (r) => !wave && periodo === "rolling" && (r.chiamate || 0) < soglia;
  const celle = (r, sotto) => (
    <>
      <td className="px-3 py-2 whitespace-nowrap">
        {espandibile && !sotto ? (
          <button type="button" onClick={() => setAperti((a) => ({ ...a, [r.nome]: !a[r.nome] }))}
            className="inline-flex items-center gap-1 hover:text-[#0018A8] transition-colors duration-150" data-testid={`${testPrefix}-apri-${slug(r.nome)}`}>
            {aperti[r.nome] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}<Nome r={r} wave={wave} />
          </button>
        ) : <Nome r={r} wave={wave || sotto} sotto={sotto} />}
      </td>
      {!agenzia && !wave && <td className="px-3 py-2 text-slate-500 whitespace-nowrap text-[11px]">{sotto ? "" : (r.agenzie || []).join(", ")}</td>}
      {periodo === "mensile" && mesi.map((m) => <CellaMese key={m} r={r.mesi?.[m]} chiuso={chiusi.includes(m)} />)}
      {periodo === "rolling" && <td className="px-3 py-2 text-right font-mono">{num(r.chiamate)}</td>}
      <td className="px-3 py-2 text-right font-mono">{num(r.clienti)}</td>
      <td className="px-3 py-2 text-right font-mono">{num(r.caricate)}</td>
      {periodo === "rolling" && <td className="px-3 py-2 text-right font-mono">{eur(r.caricato)}</td>}
      <td className="px-3 py-2 text-right font-mono">{num(r.liquidate)}</td>
      {periodo === "rolling" && <td className="px-3 py-2 text-right font-mono">{eur(r.liquidato)}</td>}
      <td className="px-3 py-2 text-right font-mono font-semibold text-[#0018A8]">{pct(r.redemption)}</td>
      <td className="px-3 py-2 text-right font-mono text-[#0F766E]">{pct(r.redemption_liquidate)}</td>
    </>
  );
  return righe.map((r) => (
    <>
      <tr key={r.nome} className={`border-b border-slate-100 hover:bg-slate-50/70 transition-colors duration-150 ${grigia(r) ? "text-slate-400 [&_td]:text-slate-400 [&_span]:text-slate-400" : ""}`}
        title={grigia(r) ? `${num(r.chiamate)} chiamate utili: sotto la soglia di ${num(soglia)}` : undefined} data-testid={`${testPrefix}-riga-${slug(r.nome)}`}>{celle(r, false)}</tr>
      {aperti[r.nome] && (r.wave || []).map((w) => (
        <tr key={`${r.nome}-${w.nome}`} className="border-b border-slate-50 bg-slate-50/50 text-xs" data-testid={`${testPrefix}-riga-${slug(r.nome)}-${slug(w.nome)}`}>{celle(w, true)}</tr>
      ))}
    </>
  ));
}

function Tabella({ dati, dimensione, periodo, agenzia }) {
  const blocco = periodo === "mensile" ? dati.mensile : dati.rolling;
  const righe = dimensione === "wave" ? blocco.wave : blocco.operatori;
  const mesi = periodo === "mensile" ? dati.mesi : [];
  const chiusi = dati.mesi_chiusi;
  const wave = dimensione === "wave";
  const tot = blocco.totale;
  const prefix = `corto-redemption-${dimensione}-${periodo}`;
  const nCol = 1 + (!agenzia && !wave ? 1 : 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid={`${prefix}-tabella`}>
        <thead>
          <tr className="text-left uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">
            <th className="px-3 py-2">{wave ? "Wave" : "Operatore"}</th>
            {!agenzia && !wave && <th className="px-3 py-2">Agenzia</th>}
            {mesi.map((m) => (
              <th key={m} className={`px-2 py-2 text-center whitespace-nowrap ${chiusi.includes(m) ? "" : "text-slate-400"}`}>{meseLabel(m)}{chiusi.includes(m) ? "" : " *"}</th>
            ))}
            {periodo === "rolling" && <th className="px-3 py-2 text-right">Chiamate utili</th>}
            <th className="px-3 py-2 text-right">Clienti risposto</th>
            <th className="px-3 py-2 text-right">Caricate</th>
            {periodo === "rolling" && <th className="px-3 py-2 text-right">Caricato</th>}
            <th className="px-3 py-2 text-right">Liquidate</th>
            {periodo === "rolling" && <th className="px-3 py-2 text-right">Liquidato</th>}
            <th className="px-3 py-2 text-right">Redemption</th>
            <th className="px-3 py-2 text-right">Red. liquidate</th>
          </tr>
        </thead>
        <tbody>
          <Righe righe={righe} periodo={periodo} mesi={mesi} chiusi={chiusi} agenzia={agenzia} wave={wave} espandibile={!wave} testPrefix={prefix} soglia={dati.soglia_chiamate || 1000} />
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-300 font-bold" data-testid={`${prefix}-totale`}>
            <td className="px-3 py-2" colSpan={nCol}>TOTALE</td>
            {mesi.map((m) => <CellaMese key={m} r={tot.mesi?.[m]} chiuso={chiusi.includes(m)} />)}
            {periodo === "rolling" && <td className="px-3 py-2 text-right font-mono">{num(tot.chiamate)}</td>}
            <td className="px-3 py-2 text-right font-mono">{num(tot.clienti)}</td>
            <td className="px-3 py-2 text-right font-mono">{num(tot.caricate)}</td>
            {periodo === "rolling" && <td className="px-3 py-2 text-right font-mono">{eur(tot.caricato)}</td>}
            <td className="px-3 py-2 text-right font-mono">{num(tot.liquidate)}</td>
            {periodo === "rolling" && <td className="px-3 py-2 text-right font-mono">{eur(tot.liquidato)}</td>}
            <td className="px-3 py-2 text-right font-mono text-[#0018A8]">{pct(tot.redemption)}</td>
            <td className="px-3 py-2 text-right font-mono text-[#0F766E]">{pct(tot.redemption_liquidate)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}


/** Grafico: per wave×mese barre raggruppate; per operatore (o rolling) barre orizzontali ordinate. */
function Grafico({ dati, dimensione, periodo, operatore, onOperatore }) {
  const blocco = periodo === "mensile" ? dati.mensile : dati.rolling;
  if (dimensione === "wave" && periodo === "mensile") {
    const serie = dati.mesi.map((m) => {
      const o = { label: `${meseLabel(m)}${dati.mesi_chiusi.includes(m) ? "" : " *"}`, TOTALE: blocco.totale.mesi?.[m]?.redemption ?? null };
      blocco.wave.forEach((w) => { o[w.nome] = w.mesi?.[m]?.redemption ?? null; });
      return o;
    });
    const keys = [{ key: "TOTALE", name: "Totale", color: COLORI.TOTALE }, ...blocco.wave.filter((w) => w.nome !== "WELCOME").map((w) => ({ key: w.nome, name: w.nome, color: COLORI[w.nome] || "#64748B" }))];
    return <ComparisonChart testId="corto-redemption-grafico" title="Redemption % per mese e wave" data={serie} keys={keys} labelFormat="perc" height={340} legenda hideLabels />;
  }
  const righe = dimensione === "wave" ? blocco.wave : blocco.operatori.filter((o) => o.clienti >= 50);
  const sottoSoglia = (r) => dimensione === "operatori" && periodo === "rolling" && (r.chiamate || 0) < (dati.soglia_chiamate || 1000);
  const serie = [...righe].sort((a, b) => (b.redemption || 0) - (a.redemption || 0))
    .map((r) => ({ label: r.nome, valore: r.redemption ?? 0, colore: dimensione === "wave" ? COLORI[r.nome] : (sottoSoglia(r) ? "#CBD5E1" : undefined), clienti: r.clienti, caricate: r.caricate, chiamate: r.chiamate }));
  serie.push({ label: "TOTALE", valore: blocco.totale.redemption ?? 0, clienti: blocco.totale.clienti, caricate: blocco.totale.caricate });
  const quando = periodo === "mensile" ? `da inizio ${dati.anno}` : `rolling ${dateIt(blocco.dal)} – ${dateIt(blocco.al)}`;
  const titolo = `Redemption % ${dimensione === "wave" ? "per wave" : "per operatore"} · ${quando}`;
  const scelto = dimensione === "operatori" ? blocco.operatori.find((o) => o.nome === operatore) : null;
  return (
    <div className={scelto ? "grid gap-4 xl:grid-cols-[3fr_2fr]" : ""}>
      <ComparisonChart testId="corto-redemption-grafico" title={titolo} data={serie} orizzontale labelFormat="perc" larghezzaEtichette={190}
        height={Math.max(220, 26 * serie.length + 40)} keys={[{ key: "valore", name: "Redemption", color: "#0018A8" }]}
        tooltipExtra={(p) => (p ? `${num(p.caricate)}/${num(p.clienti)}${dimensione === "operatori" && periodo === "rolling" ? ` · ${num(p.chiamate)} chiamate utili` : ""}` : null)}
        onBarClick={dimensione === "operatori" ? (p) => onOperatore(p.label === "TOTALE" || p.label === operatore ? null : p.label) : undefined} />
      {dimensione === "operatori" && periodo === "rolling" && (
        <div className="text-[11px] text-slate-400 px-4 -mt-2" data-testid="corto-redemption-soglia-nota">
          In grigio gli operatori con meno di {num(dati.soglia_chiamate || 1000)} chiamate utili (risposte) nel periodo di osservazione.
        </div>
      )}
      {scelto && <GraficoWaveOperatore operatore={scelto} quando={quando} onChiudi={() => onOperatore(null)} />}
    </div>
  );
}

function GraficoWaveOperatore({ operatore, quando, onChiudi }) {
  const serie = operatore.wave.map((w) => ({ label: w.nome, valore: w.redemption ?? 0, colore: COLORI[w.nome] || "#64748B", clienti: w.clienti, caricate: w.caricate }));
  return (
    <div className="relative" data-testid="corto-redemption-grafico-operatore-wave">
      <button type="button" onClick={onChiudi} data-testid="corto-redemption-grafico-operatore-chiudi"
        className="absolute right-3 top-3 z-10 text-[11px] text-slate-500 hover:text-slate-900 transition-colors duration-150">chiudi ✕</button>
      <ComparisonChart title={`${operatore.nome} · redemption per wave · ${quando}`} data={serie} orizzontale labelFormat="perc" larghezzaEtichette={110}
        height={Math.max(220, 34 * serie.length + 40)} keys={[{ key: "valore", name: "Redemption", color: "#0018A8" }]}
        tooltipExtra={(p) => (p ? `${num(p.caricate)}/${num(p.clienti)}` : null)} />
      <div className="flex flex-wrap gap-2 px-4 pb-3 -mt-2 text-[11px] text-slate-500">
        {serie.map((w) => <span key={w.label}><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: w.colore }} />{w.label}: {num(w.caricate)} su {num(w.clienti)}</span>)}
      </div>
    </div>
  );
}

/** Redemption CRM (regola 90 giorni): per wave o per operatore, coorti mensili o rolling 90 giorni. */
export function RedemptionCrm({ anno = 2026, agenzia = "", testId = "corto-redemption" }) {
  const { user } = useAuth();
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState("");
  const [dimensione, setDimensione] = useState("wave");
  const [periodo, setPeriodo] = useState("mensile");
  const [vista, setVista] = useState("grafico");
  const [operatore, setOperatore] = useState(null);
  const [avvio, setAvvio] = useState(false);
  const puoAggiornare = (user?.funzioni || []).includes("corto_sincronizza_crm") || ["MASTER", "ADMIN"].includes(user?.role);

  const carica = () => api.get(`/corto/redemption-crm?anno=${anno}${agenzia ? `&agenzia=${encodeURIComponent(agenzia)}` : ""}`)
    .then((r) => { setDati(r.data); setErrore(""); }).catch((e) => setErrore(apiError(e)));
  useEffect(() => { carica(); }, [anno, agenzia]); // eslint-disable-line react-hooks/exhaustive-deps
  const inCorso = !!dati?.sync?.in_corso;
  useEffect(() => {
    if (!inCorso) return undefined;
    const t = setInterval(carica, 30000);
    return () => clearInterval(t);
  }, [inCorso]); // eslint-disable-line react-hooks/exhaustive-deps

  const aggiorna = async () => {
    setAvvio(true);
    try {
      const r = await api.post("/corto/redemption-crm/aggiorna");
      toast.success(r.data.gia_in_corso ? "Lettura già in corso" : "Lettura telefonate dal CRM avviata");
      setTimeout(carica, 3000);
    } catch (e) { toast.error(apiError(e)); } finally { setAvvio(false); }
  };

  if (!dati) {
    return (
      <Card className="p-4" data-testid={testId}>
        <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">Redemption CRM · regola 90 giorni</h3>
        <div className="text-sm text-slate-500 mt-2" data-testid="corto-redemption-caricamento">
          {errore ? `Dati non disponibili: ${errore}` : "Caricamento…"}
        </div>
      </Card>
    );
  }
  const sync = dati.sync || {};
  const vuoto = !dati.mensile.totale.clienti && !dati.mensile.totale.caricate;
  return (
    <Card className="p-0 overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">Redemption CRM · regola 90 giorni</h3>
          <div className="text-xs text-slate-400 mt-0.5" data-testid="corto-redemption-periodo">
            {periodo === "mensile"
              ? "coorti per mese della telefonata · * mese non ancora chiuso"
              : `telefonate risposte dal ${dateIt(dati.rolling.dal)} al ${dateIt(dati.rolling.al)} · pratiche nate entro 90 giorni`}
            {sync.ultimo_giorno ? ` · telefonate CRM lette fino al ${dateIt(sync.ultimo_giorno)}` : ""}
          </div>
          {sync.in_corso && (
            <div className="text-xs text-[#0018A8] mt-1 font-semibold" data-testid="corto-redemption-sync-stato">
              Lettura dal CRM in corso · {sync.fase}{sync.totali ? ` · ${num(sync.fatte || 0)} / ${num(sync.totali)}` : ""}{sync.giorno ? ` · ${dateIt(sync.giorno)}` : ""}
            </div>
          )}
          {!sync.in_corso && sync.errore && (
            <div className="text-xs text-[#C8102E] mt-1" data-testid="corto-redemption-sync-errore">Ultima lettura interrotta: {sync.errore}</div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Toggle valore={dimensione} onChange={setDimensione} testId="corto-redemption-dimensione"
            opzioni={[["wave", Layers, "Per wave"], ["operatori", Headset, "Per operatore"]]} />
          <Toggle valore={periodo} onChange={setPeriodo} testId="corto-redemption-tipo"
            opzioni={[["mensile", CalendarRange, "Per mese"], ["rolling", CalendarRange, "Rolling 90 gg"]]} />
          <Toggle valore={vista} onChange={setVista} testId="corto-redemption-vista"
            opzioni={[["grafico", BarChart3, "Grafico"], ["tabella", Table2, "Tabella"]]} />
          {puoAggiornare && (
            <button type="button" onClick={aggiorna} disabled={avvio || inCorso} data-testid="corto-redemption-aggiorna"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0018A8] hover:underline disabled:opacity-50">
              <RefreshCw size={13} className={avvio || inCorso ? "animate-spin" : ""} /> {inCorso ? "Lettura in corso…" : "Rileggi telefonate dal CRM"}
            </button>
          )}
        </div>
      </div>
      {vuoto ? (
        <div className="px-4 py-6 text-sm text-slate-400" data-testid="corto-redemption-vuoto">
          {inCorso ? "Lettura delle telefonate dal CRM in corso: la tabella si popola man mano." : "Nessuna telefonata letta: usa \"Rileggi telefonate dal CRM\"."}
        </div>
      ) : vista === "grafico" ? (
        <div className="p-4"><Grafico dati={dati} dimensione={dimensione} periodo={periodo} operatore={operatore} onOperatore={setOperatore} /></div>
      ) : <Tabella dati={dati} dimensione={dimensione} periodo={periodo} agenzia={agenzia} />}
    </Card>
  );
}
